import axiosApiClient from '..';
import { handleError, throwErrorIfWrongStatus } from './utils';

const BASE_URL = '/d/crm-organizations';

class ChatterService {
  changePassword = async ({ currentPassword, password }) => {
    try {
      const body = {
        currentPassword,
        password,
      };
      const response = await axiosApiClient.put(`${BASE_URL}/change-password`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, "Error occurred while changing user's password.");
    }
  };

  fetchInfluencerInfo = async ({ page, perPage, search }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('perPage', perPage);
      if (search) params.append('search', search);
      const response = await axiosApiClient.get(`${BASE_URL}/influencers?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total } = response.data.data;
      return { data, total };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching influencer's info.");
    }
  };

  fetchInfluencerById = async ({ influencerId }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/influencers/${influencerId}`);
      throwErrorIfWrongStatus(response, 200);
      const { influencer } = response.data.data;
      return { influencer };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching influencer's info.");
    }
  };

  generateInfluencerPersonality = async ({ influencerId, photos }) => {
    try {
      const formData = new FormData();
      photos.forEach(photo => formData.append('photos', photo));
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/influencers/${influencerId}/personality`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { personality } = response.data.data;
      return { personality };
    } catch (err) {
      throw handleError(err, "Error occurred while generating influencer's personality.");
    }
  };

  uploadInfluencerVoice = async ({ influencerId, audio }) => {
    try {
      const formData = new FormData();
      formData.append('file', audio.file, audio.file.name);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/influencers/${influencerId}/voice/upload`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { voiceId } = response.data.data;
      return { voiceId };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading audio.');
    }
  };

  uploadInfluencerQuestionnaire = async ({ influencerId, file }) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(
        `${BASE_URL}/influencers/${influencerId}/facts/questionnaire`,
        formData,
        { headers },
      );
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading questionnaire.');
    }
  };

  testInfluencerVoice = async ({ influencerId, voiceId, stability, message }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/influencers/${influencerId}/voice/test`, {
        voiceId,
        stability,
        message,
      });
      throwErrorIfWrongStatus(response, 200);
      const { speech } = response.data.data;
      return { speech };
    } catch (err) {
      throw handleError(err, 'Error occurred while testing voice.');
    }
  };

  saveInfluencerVoice = async ({ influencerId, voiceId, stability }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/influencers/${influencerId}/voice`, {
        voiceId,
        stability,
      });
      throwErrorIfWrongStatus(response, 200);
      return {};
    } catch (err) {
      throw handleError(err, 'Error occurred while saving voice.');
    }
  };

  // Prices are in USD, server converts them to stars
  editInfluencerPricing = async ({ influencerId, boundaries }) => {
    try {
      const response = await axiosApiClient.put(`${BASE_URL}/influencers/${influencerId}/pricing`, {
        pricing: { boundaries },
      });
      throwErrorIfWrongStatus(response, 200);
      const { pricing } = response.data.data;
      return { pricing };
    } catch (err) {
      throw handleError(err, 'Error occurred while saving pricing.');
    }
  };

  fetchOrganizationMembersInfo = async ({ page, perPage, search }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('perPage', perPage);
      if (search) params.append('search', search);
      const response = await axiosApiClient.get(`${BASE_URL}/members?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total } = response.data.data;
      return { data, total };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching members's info.");
    }
  };

  fetchAllOrganizationInfluencers = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/influencers/all`);
      throwErrorIfWrongStatus(response, 200);
      const { influencers } = response.data.data;
      return { influencers };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching influencers.');
    }
  };

  createOrganizationMember = async ({ name, email, password, role, influencerIds, chatterIds, teamLeadId }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/members`, {
        name,
        email,
        password,
        role,
        influencerIds,
        chatterIds,
        teamLeadId,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while creating organization member.');
    }
  };

  fetchAllOrganizationChatters = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/chatters/all`);
      throwErrorIfWrongStatus(response, 200);
      const { chatters } = response.data.data;
      return { chatters };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching chatters.');
    }
  };

  fetchAllOrganizationTeamLeads = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/team-leads/all`);
      throwErrorIfWrongStatus(response, 200);
      const { teamLeads } = response.data.data;
      return { teamLeads };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching team leads.');
    }
  };

  deleteOrganizationMember = async ({ memberId }) => {
    try {
      const response = await axiosApiClient.delete(`${BASE_URL}/members/${memberId}`);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while deleting organization member.');
    }
  };

  getOrganizationMemberById = async ({ memberId }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/members/${memberId}`);
      throwErrorIfWrongStatus(response, 200);
      const { member } = response.data.data;
      return { member };
    } catch (err) {
      throw handleError(err, 'Error occurred while getting organization member.');
    }
  };

  editOrganizationMember = async ({ memberId, name, chatterIds, teamLeadId, influencerIds }) => {
    try {
      const response = await axiosApiClient.put(`${BASE_URL}/members/${memberId}`, {
        name,
        chatterIds,
        teamLeadId,
        influencerIds,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while editing organization member.');
    }
  };

  createReview = async ({ reason, description, interactionId }) => {
    try {
      const body = {
        reason,
        interactionId,
      };
      if (description !== undefined) body.description = description;
      const response = await axiosApiClient.post(`${BASE_URL}/reviews`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while creating review.');
    }
  };

  fetchReviews = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/reviews`);
      throwErrorIfWrongStatus(response, 200);
      const { reviews } = response.data.data;
      return { reviews };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching reviews.');
    }
  };

  fetchReviewById = async ({ reviewId }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/reviews/${reviewId}`);
      throwErrorIfWrongStatus(response, 200);
      const { review } = response.data.data;
      return { review };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching review.');
    }
  };
}

export default new ChatterService();
